import { concat, get, has, isEmpty, set } from "lodash";
import { baseToast, ToastType } from "@/helpers/toastification";
import { capitalize, defineComponent } from "vue";
import {
  buildDataFromSchema,
  getDataFromContext,
  resolveCondition,
  resolveContextPath,
  resolveValues,
} from "@/helpers/utilsV2";
import { ContextKeys } from "@/enums/contextKeys.enum";
import Drivers from "@/helpers/clients/Drivers";
import { AxiosResponse } from "axios";

import {
  Action,
  AppendAction,
  ClientSideEvents,
  EmitAction,
  InterruptAction,
  Lifecycle,
  NotifyAction,
  PipelineSettings,
  PipelineStep,
  RetryAction,
  SetAction,
} from "@/types";
import {
  BaseActionError,
  InterruptLifecycleActionError,
  InterruptPipelineActionError,
  InterruptPipelineStepActionError,
  RetryActionError,
} from "@/core/errors/actions/BaseActionError";
import { PipelineScope } from "@/enums/pipelineScope.enum";

const driversClient = new Drivers();

interface ExecuteActionParam<T extends Action = Action> {
  action: T;
  context?: any;
  meta?: any;
}

interface ProcessEventParam {
  events: ClientSideEvents;
  eventName: string;
  context?: Record<string, unknown>;
  meta?: Record<string, unknown>;
}

interface ProcessPipelineParams {
  pipelineSettings: PipelineSettings;
  context?: Record<string, unknown>;
  meta?: Record<string, unknown>;
}

interface ProcessPipelineStepParams {
  step: PipelineStep;
  context?: Record<string, unknown>;
  meta?: Record<string, unknown>;
}

interface ExecuteHookActionsParams {
  settings: {
    lifecycle: Lifecycle;
  };
  hook?: string;
  context?: Record<string, unknown>;
  meta?: Record<string, unknown>;
}

interface ExecuteActionsParam<T extends Action = Action> {
  actions: T[];
  context?: any;
  meta?: any;
}

export default defineComponent({
  methods: {
    async processEvent({ events, eventName, context = {}, meta = {} }: ProcessEventParam): Promise<void> {
      set(meta, ContextKeys.EVENT, events[eventName]);

      await this.processPipeline({ pipelineSettings: get(events, eventName, {}) as PipelineSettings, context, meta });
    },

    async processPipeline({ pipelineSettings, context = {}, meta = {} }: ProcessPipelineParams): Promise<void> {
      await this.executeBeforeActions({
        settings: pipelineSettings.settings,
        context: context,
        meta: meta,
      });

      for (const pipelineStep of get(pipelineSettings, "pipeline", [])) {
        try {
          set(meta, ContextKeys.PIPELINE_STEP, pipelineStep);
          await this.processPipelineStep({ step: pipelineStep, context: context, meta: meta });
        } catch (error: unknown) {
          if (error instanceof BaseActionError) {
            throw error;
          } else if (error instanceof InterruptPipelineActionError) {
            throw error;
          }
        }
      }

      await this.executeAfterActions({ settings: pipelineSettings.settings, context: context, meta: meta });
    },

    async processPipelineStep({ step, context = {}, meta = {} }: ProcessPipelineStepParams): Promise<void> {
      try {
        await this.executeBeforeActions({ settings: step.settings, context: context, meta: meta });

        if (has(step, "driver.id")) {
          set(
            context,
            ContextKeys.RESPONSE,
            await this.executeDriver({ driver: step.driver, context: context }),
          );
        }

        await this.executeAfterActions({
          settings: step.settings,
          context: context,
          meta: meta,
        });
      } catch (error: unknown) {
        if (error instanceof RetryActionError) {
          throw error;
        } else if (error instanceof InterruptPipelineActionError) {
          throw error;
        } else if (error instanceof InterruptPipelineStepActionError) {
          throw error;
        }else {
          set(context, ContextKeys.RESPONSE, get(error, "response", null));
          await this.executeOnExceptionActions({
            settings: step.settings,
            context: context,
            meta: meta,
          });
        }
      }
    },

    async executeDriver({ driver, context }: { driver: any; context: any }): Promise<AxiosResponse> {
      return driversClient.execute(
        driver.id,
        buildDataFromSchema({
          schema: get(driver, "data_schema", {}),
          context: context,
        }),
      );
    },

    async executeAfterActions(
      { settings, hook = "after", context = {}, meta = {} }: ExecuteHookActionsParams,
    ): Promise<void> {
      await this.executeActions({
        actions: get(settings, `lifecycle.${hook}.actions`, []),
        context: context,
        meta: meta,
      });
    },

    async executeBeforeActions(
      { settings, hook = "before", context = {}, meta = {} }: ExecuteHookActionsParams,
    ): Promise<void> {
      await this.executeActions({
        actions: get(settings, `lifecycle.${hook}.actions`, []),
        context: context,
        meta: meta,
      });
    },

    async executeOnExceptionActions(
      { settings, hook = "onException", context = {}, meta = {} }: ExecuteHookActionsParams,
    ): Promise<void> {
      await this.executeActions({
        actions: get(settings, `lifecycle.${hook}.actions`, []),
        context: context,
        meta: meta,
      });
    },

    async executeAction({ action, context = {}, meta = {} }: ExecuteActionParam): Promise<void> {
      try {
        await (this as any)[`execute${capitalize(action.type)}Action`]({
          action: action,
          context: context,
          meta: meta,
        });
      } catch (error: unknown) {
        if (error instanceof BaseActionError) {
          throw error;
        }
      }
    },

    async executeActions({ actions, context = {}, meta = {} }: ExecuteActionsParam): Promise<void> {
      for (const action of actions) {
        const conditionsMet: boolean = get(action, "settings.conditions", []).every((condition: any): boolean =>
          resolveCondition(condition.type, resolveValues(condition.values, context)),
        );

        if (conditionsMet) {
          try {
            await this.executeAction({ action: action, context: context, meta: meta });
          } catch (error: unknown) {
            if (error instanceof BaseActionError) {
              throw error;
            }
          }
        }
      }
    },

    async executeAppendAction({ action, context }: ExecuteActionParam<AppendAction>): Promise<void> {
      let targetPath: string = get(action, "settings.target", "");
      let value: any =
        get(action, "settings.value") ??
        get(context, get(action, "settings.source", ""), []) ??
        get(context, get(action, "settings.default", []), []);

      const [prefix, ...rest] = targetPath.split(".");
      targetPath = rest.join(".");

      set(context[prefix], rest.join("."), concat(get(context[prefix], targetPath, []), value));
    },

    async executeEmitAction({ action, context }: ExecuteActionParam<EmitAction>): Promise<void> {
      this.$emitter.emit(
        resolveContextPath(action.settings.event_name, this),
        JSON.parse(
          JSON.stringify(get(action.settings, "data", {})),
          (_: string, value: any): any => {
            if (typeof value === "string" && value.startsWith("$")) {
              return getDataFromContext({path: value, context: context});
            }

            return value;
          }
        ),
      );
    },

    async executeRetryAction({ action, context = {}, meta = {} }: ExecuteActionParam<RetryAction>): Promise<void> {
      let attempt: number = get(action, "settings.retryPolicy.attempt", 1);

      if (attempt <= get(action, "settings.retryPolicy.maxTries", 5)) {
        set(action, "settings.retryPolicy.attempt", attempt + 1);
        await new Promise((resolve: any): void => {
          setTimeout(resolve, get(action, "settings.retryPolicy.delayMs", 3000));
        });

        if (action.settings.retryPolicy.scope === PipelineScope.PIPELINE_STEP) {
          await this.processPipelineStep({
            step: get(meta, ContextKeys.PIPELINE_STEP, {}) as PipelineStep,
            context: context,
            meta: meta,
          });
        } else if (action.settings.retryPolicy.scope === PipelineScope.PIPELINE) {
          await this.processPipeline({
            pipelineSettings: get(meta, ContextKeys.EVENT, {}),
            context: context,
            meta: meta,
          });
        }
      } else {
        set(action, "settings.retryPolicy.attempt", 1);

        const message = get(action, "settings.retryPolicy.onExhausted.message", "");

        if (!isEmpty(message.trim())) {
          baseToast({
            title: message,
            customOptions: {
              timeout: 5000,
            },
          });
        }

        if (get(action, "settings.retryPolicy.onExhausted.interrupt", false)) {
          throw new RetryActionError(action, "", context, null);
        }
      }
    },

    executeInterruptAction({ action, context = {}, meta = {} }: ExecuteActionParam<InterruptAction>): void {
      const messageValue: string = get(action, "settings.message.value", "");

      if (messageValue) {
        baseToast({
          title: messageValue,
        });
      } else {
        let errorMessage = getDataFromContext({
          path: get(action, "settings.message.source", ""),
          context
        });

        try {
          errorMessage = JSON.parse(errorMessage);
          for (const [key, value] of Object.entries(errorMessage)) {
            if (typeof value === "string") {
              errorMessage[key] = value.replaceAll("\"", "");
            }
          }

          errorMessage = JSON.stringify(errorMessage);
        } catch (_) {
        }

        baseToast({
          title: errorMessage,
        });
      }

      if (action.settings.scope === PipelineScope.PIPELINE) {
        throw new InterruptPipelineActionError(action, "", context, null);
      } else if (action.settings.scope === PipelineScope.PIPELINE_STEP) {
        throw new InterruptPipelineStepActionError(action, "", context, null);
      } else {
        throw new InterruptLifecycleActionError(action, messageValue, context, null);
      }
    },

    async executeNotifyAction({ action }: ExecuteActionParam<NotifyAction>): Promise<void> {
      const toastType = get(action, "settings.type", "error") as keyof typeof ToastType;

      baseToast(
        {
          title: get(action, "settings.message", "Ha ocurrido un error"),
          customOptions: { timeout: 5000 },
          ...get(action, "settings", {}),
        },
        toastType,
      );
    },

    async executeSetAction({ action, context }: ExecuteActionParam<SetAction>): Promise<void> {
      let targetPath: string = get(action, "settings.target", "");
      let value: any =
        get(action, "settings.value") ??
        getDataFromContext({ path: get(action, "settings.source", ""), context: context }) ??
        get(context, get(action, "settings.default", ""), null);

      const [prefix, ...rest] = targetPath.split(".");

      set(context[prefix], rest.join("."), value);
    },
  },
});